import { renderToStaticMarkup } from 'react-dom/server'
import type { Slide } from '~/design-system/components/DeckGen/types/presentation'
import { ElementVisual } from '~/design-system/components/DeckGen/components/editor/ElementVisual'
import { gradientToCss } from '~/design-system/components/DeckGen/utils/gradientFill'
import { SLIDE_SIZE } from './constants'

/**
 * Element-authored slides carry NO `code.html` — their content lives entirely in `elements`. Without an
 * html string the far-zoom drawer has nothing to rasterize, so it falls back to the gray element-block
 * "sketch" (what reads as a skeleton at overview zoom). This gives those slides the SAME real thumbnail the
 * html slides get: render their elements through the exact pure renderer the live DOM twin uses
 * (`ElementVisual`) into a static HTML string, which the thumbnail pipeline rasterizes once and draws crisp
 * at every zoom. Inline data-URL images embed as-is; fonts are embedded by the rasterizer. The wrapper
 * mirrors LabSlide's flat-visual branch 1:1, so the thumbnail and the mounted twin are pixel-identical (the
 * bitmap↔DOM hand-off is a pure sharpen, no jump).
 *
 * Cached per slide id + updatedAt so a re-import (or the route + canvas both building shapes) renders each
 * slide once; an edit bumps updatedAt and busts it.
 */
const cache = new Map<string, string>()

export function renderSlideElementsToHtml(slide: Slide): string {
  const key = `${slide.id}:${slide.updatedAt}`
  const hit = cache.get(key)
  if (hit) return hit
  const background = slide.fillGradient
    ? gradientToCss(slide.fillGradient, { width: SLIDE_SIZE.w, height: SLIDE_SIZE.h })
    : (slide.background ?? '#ffffff')
  const markup = renderToStaticMarkup(
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: SLIDE_SIZE.w,
        height: SLIDE_SIZE.h,
        background,
        overflow: 'hidden',
      }}
    >
      {/* Containers (group/stack) are LOGICAL — only their leaves paint. Rendering them would fall through
          ElementVisual to the shape branch and draw a stray default-border box. */}
      {(slide.elements ?? [])
        .filter((el) => el.type !== 'group' && el.type !== 'stack')
        .map((el) => (
          <div
            key={el.id}
            style={{
              position: 'absolute',
              left: el.x,
              top: el.y,
              width: el.width,
              height: el.height,
              zIndex: el.zIndex,
            }}
          >
            <ElementVisual element={el} />
          </div>
        ))}
    </div>,
  )
  cache.set(key, markup)
  return markup
}
